import { PDFArray, PDFDocument, PDFHexString, PDFName, PDFNumber } from "pdf-lib";

export type C1_PdfOutlineEntry = {
  title: string;
  pageIndex: number;
  children?: C1_PdfOutlineEntry[];
};

export function addC1PdfOutlines(document: PDFDocument, entries: C1_PdfOutlineEntry[]): void {
  const pages = document.getPages();
  const validEntries = entries.filter((entry) => entry.title.trim() && entry.pageIndex >= 0 && entry.pageIndex < pages.length);
  if (!validEntries.length) return;

  const context = document.context;
  const outlineRef = context.nextRef();
  const createLevel = (levelEntries: C1_PdfOutlineEntry[], parentRef: ReturnType<typeof context.nextRef>) => {
    const filtered = levelEntries.filter((entry) => entry.title.trim() && pages[entry.pageIndex]);
    const refs = filtered.map(() => context.nextRef());
    filtered.forEach((entry, index) => {
      const children = entry.children?.filter((child) => child.title.trim() && pages[child.pageIndex]) ?? [];
      const childRefs = children.length ? createLevel(children, refs[index]) : [];
      context.assign(refs[index], context.obj({
        Title: PDFHexString.fromText(entry.title.trim()),
        Parent: parentRef,
        Dest: context.obj([pages[entry.pageIndex].ref, PDFName.of("Fit")]),
        Prev: index > 0 ? refs[index - 1] : undefined,
        Next: index < refs.length - 1 ? refs[index + 1] : undefined,
        First: childRefs[0],
        Last: childRefs.at(-1),
        Count: childRefs.length ? PDFNumber.of(childRefs.length) : undefined,
      }));
    });
    return refs;
  };
  const itemRefs = createLevel(validEntries, outlineRef);

  context.assign(
    outlineRef,
    context.obj({
      Type: PDFName.of("Outlines"),
      First: itemRefs[0],
      Last: itemRefs[itemRefs.length - 1],
      Count: PDFNumber.of(itemRefs.length),
    }),
  );
  document.catalog.set(PDFName.of("Outlines"), outlineRef);
  document.catalog.set(PDFName.of("PageMode"), PDFName.of("UseOutlines"));
}

export type C1_PdfPageLink = {
  sourcePageIndex: number;
  targetPageIndex: number;
  x: number;
  y: number;
  width: number;
  height: number;
};

export function addC1PdfPageLinks(document: PDFDocument, links: C1_PdfPageLink[]): void {
  const pages = document.getPages();
  const context = document.context;
  links.forEach((link) => {
    const sourcePage = pages[link.sourcePageIndex];
    const targetPage = pages[link.targetPageIndex];
    if (!sourcePage || !targetPage || link.width <= 0 || link.height <= 0) return;

    const pageHeight = sourcePage.getHeight();
    const annotation = context.obj({
      Type: PDFName.of("Annot"),
      Subtype: PDFName.of("Link"),
      Rect: context.obj([
        link.x,
        pageHeight - link.y - link.height,
        link.x + link.width,
        pageHeight - link.y,
      ]),
      Border: context.obj([0, 0, 0]),
      Dest: context.obj([targetPage.ref, PDFName.of("Fit")]),
    });
    const annotationRef = context.register(annotation);
    const annotsKey = PDFName.of("Annots");
    let annotations = sourcePage.node.lookupMaybe(annotsKey, PDFArray);
    if (!annotations) {
      annotations = context.obj([]);
      sourcePage.node.set(annotsKey, annotations);
    }
    annotations.push(annotationRef);
  });
}
